/*
 *
 * This script is the enry point of the simulation.
 * It is responsible for displaying the initial view and calling the `Vacuumworld` script.
 *
 */

import { DOMPurifyPolicy } from "./TTPolicies";
import { VWTheme } from "./gui/theme/VWTheme";
import { VWThemeUtils } from "./gui/theme/VWThemeUtils";

import globalGUIConfig from "./gui/common/VWGui.json";

const GUI = globalGUIConfig.GUI;
const errorDivData = GUI.ErrorDiv;

export class Main {
    private static NONCE: string = undefined;
    private constructor() {}

    public static setNonce(): void {
        const indexScriptID: string = GUI.IndexScript.ID;

        if (document.getElementById(indexScriptID).nonce !== null && document.getElementById(indexScriptID).nonce !== undefined) {
            // This is to propagate the `nonce` attribute to dynamically created/loaded scripts.
            // Main.NONCE is private, so it can't be accessed from outside the class.
            // This script should have a `nonce` attribute (set by the server).
            Main.NONCE = document.getElementById(indexScriptID).nonce;
        }
    }

    public static setTitle(): void {
        document.title = GUI.Metadata.Title;
    }

    public static loadCharset(): void {
        let charset: HTMLMetaElement = document.createElement("meta");

        charset.setAttribute("charset", "utf-8");

        document.head.appendChild(charset);
    }

    public static initErrorDiv(): void {
        document.getElementById(errorDivData.ID).addEventListener("click", () => {
            document.getElementById(errorDivData.ID).hidden = true;
        });
    }

    public static createToggleThemeDiv(): void {
        const toggleThemeDiv: HTMLDivElement = document.createElement("div");

        toggleThemeDiv.id = GUI.ToggleThemeDiv.ID;
        toggleThemeDiv.classList.add(...GUI.ToggleThemeDiv.ClassList);
        toggleThemeDiv.hidden = false;

        const toggleThemeButton: HTMLButtonElement = document.createElement("button");

        toggleThemeButton.id = GUI.ToggleThemeDiv.ToggleThemeButton.ID;
        toggleThemeButton.classList.add(...GUI.ToggleThemeDiv.ToggleThemeButton.ClassList);

        const currentTheme: VWTheme = localStorage.getItem("theme") as VWTheme ?? VWTheme.DARK;

        toggleThemeButton.textContent = currentTheme === VWTheme.LIGHT ? GUI.ToggleThemeDiv.ToggleThemeButton.TextDark : GUI.ToggleThemeDiv.ToggleThemeButton.TextLight;
        toggleThemeButton.addEventListener("click", () => {
            const currentTheme: VWTheme = localStorage.getItem("theme") as VWTheme ?? VWTheme.DARK;
            const newTheme: VWTheme = currentTheme === VWTheme.DARK ? VWTheme.LIGHT : VWTheme.DARK;

            localStorage.setItem("theme", newTheme);

            console.log(`Switching from ${currentTheme} theme to ${newTheme} theme.`);

            VWThemeUtils.adjustComponentsToTheme(newTheme);
        });

        toggleThemeDiv.appendChild(toggleThemeButton);

        document.body.appendChild(toggleThemeDiv);
    }

    public static loadStyles(): void {
        const stylesDir: string = globalGUIConfig.GUI.Stylesheets.StylesheetsDir;
        const styles: string[] = globalGUIConfig.GUI.Stylesheets.Stylesheets;

        styles.forEach((style: string) => {
            Main.loadStyle(`${stylesDir}/${style}`);
        });
    }

    private static loadStyle(stylePath: string): void {
        if (stylePath === null || stylePath === undefined) {
            throw new Error("The style path is null or undefined.");
        }
        else if (typeof stylePath !== "string") {
            throw new Error("The style path is not a string.");
        }
        else if (stylePath.length === 0) {
            throw new Error("The style path is empty.");
        }
        else if (stylePath.indexOf(".css") === -1) {
            throw new Error("The style path does not contain a .css file.");
        }
        else {
            let mainStyle: HTMLLinkElement = document.createElement("link");

            mainStyle.href = DOMPurifyPolicy.createScriptURL(stylePath) as TrustedScriptURL&string; // Why not :)
            mainStyle.rel = "stylesheet";
            mainStyle.type = "text/css";

            document.head.appendChild(Main.propagateNonceToElement(mainStyle));
        }
    }

    private static propagateNonceToElement(element: HTMLElement): HTMLElement {
        if (element !== null && element !== undefined && Main.NONCE?.length > 0) {
            element.setAttribute("nonce", Main.NONCE);
        }

        return element;
    }

    public static disableFurtherScriptsLoading(): void {
        let metaTag = document.createElement("meta");

        metaTag.setAttribute("http-equiv", "Content-Security-Policy");
        metaTag.setAttribute("content", "script-src 'none';");

        document.head.appendChild(metaTag);
    }
}
